import * as React from 'react'

import { Api, PartialArticle } from '@doist/help-center-utils'
import { AutocompleteSearch } from '@doist/marketist'
import { AutocompleteSearchResult } from '@doist/marketist/dist/components/AutocompleteSearch/AutocompleteSearch'

import cn from 'classnames'
import HtmlToReact from 'html-react-parser'
import debounce from 'lodash/debounce'
import { useTranslation } from 'next-i18next'

import css from './SearchBar.module.css'

export type SearchBarProps = {
    title: string
    language: string
    initialSearchText?: string
    applyHeaderStyle?: boolean
    className?: string
} & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>

export function SearchBar({
    title,
    initialSearchText = '',
    language,
    className,
    applyHeaderStyle = false,
    ...rest
}: SearchBarProps) {
    const { t } = useTranslation('common')
    const [searchResults, setSearchResults] = React.useState<AutocompleteSearchResult[]>([])
    const [searchText, setSearchText] = React.useState(initialSearchText)

    async function doSearch(searchString?: string) {
        if (!searchString || searchString.length === 0) {
            setSearchResults([])
            return
        }

        try {
            const api = new Api(process.env.ZENDESK_API_URL, '', language)
            const results = await api.searchArticles(searchString, language)
            /** Shows only the first 4 results. */
            const normResults: AutocompleteSearchResult[] = results
                .slice(0, 4)
                .map((result: PartialArticle) => ({
                    id: `${result.id}`,
                    title: result.title,
                    href: result.href,
                    description: HtmlToReact(result.snippet ?? ''),
                }))
            setSearchResults(normResults)
        } catch {
            setSearchResults([])
        }
    }
    const debouncedSearch = React.useRef(debounce(doSearch, 200))

    return (
        <div className={cn(css.searchBar, className)} {...rest}>
            <AutocompleteSearch
                htmlFor="article-search"
                placeholder={title}
                aria-label={title}
                value={searchText}
                searchResults={searchResults}
                searchLabel={t('search')}
                clearLabel={t('clearSearch')}
                onChange={(text: string) => {
                    setSearchText(text)
                    debouncedSearch.current(text)
                }}
                onSubmit={(text: string) => {
                    if (text.length > 0) {
                        location.href = '/help/search?query=' + encodeURIComponent(text)
                    }
                }}
                applyHeaderStyle={applyHeaderStyle}
            />
        </div>
    )
}
